import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Gift, Copy, Check, Users, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import MatrixShineCard from '@/components/MatrixShineCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralProgram = ({ open, onOpenChange, user, trialUser }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    successfulReferrals: 0,
    pendingReferrals: 0,
    creditsEarned: 0
  });
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  useEffect(() => {
    if (open && (user || trialUser)) {
      fetchReferralData();
    }
  }, [open, user, trialUser]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id || trialUser?.id || 'trial';
      
      const response = await axios.get(`${API}/referral/data`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { userId }
      });

      setReferralCode(response.data.referralCode);
      setReferralStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      // Generate a simple referral code if API fails
      const simpleCode = `${(user?.full_name || trialUser?.name || 'USER').toUpperCase().replace(/\s/g, '')}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      setReferralCode(simpleCode);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="account-settings-modal max-w-3xl max-h-[90vh]">
        <DialogHeader className="sticky top-0 bg-[rgba(15,15,20,0.95)] z-10 pb-4">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ color: '#ffffff', fontSize: '1.75rem', fontWeight: '700' }}>
            <Gift className="text-[#D4AF37]" size={28} />
            Referral Program
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-100px)] pr-2">
          <div className="space-y-6 mt-4">
          {/* Referral Benefits */}
          <MatrixShineCard className="rounded-xl border shadow border-[#D4AF37]">
            <div className="pt-6 pb-6 px-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <Award className="w-12 h-12 mx-auto mb-2 text-[#D4AF37]" />
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '700' }}>You Get</h3>
                  <p className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#E5B200] bg-clip-text text-transparent">
                    +50
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Bonus Credits</p>
                </div>
                <div>
                  <Gift className="w-12 h-12 mx-auto mb-2 text-[#D4AF37]" />
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '700' }}>They Get</h3>
                  <p className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#E5B200] bg-clip-text text-transparent">
                    +20
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Trial Credits</p>
                </div>
              </div>
            </div>
          </MatrixShineCard>

          {/* Referral Link */}
          <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
            <CardHeader>
              <CardTitle style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Your Referral Link</CardTitle>
              <CardDescription style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Share this link with colleagues and friends
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={referralLink}
                  readOnly
                  className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                />
                <Button
                  onClick={copyToClipboard}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black font-bold hover:from-[#FFCE45] hover:to-[#E5B200] shrink-0"
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <p className="text-sm mt-3" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Your referral code: <span className="font-bold text-[#D4AF37]">{referralCode}</span>
              </p>
            </CardContent>
          </Card>

          {/* Referral Statistics */}
          <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
            <CardHeader>
              <CardTitle style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Your Referral Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-[rgba(30,30,35,0.8)] rounded-lg border border-[rgba(212,175,55,0.2)]">
                  <Users className="w-8 h-8 mx-auto mb-2 text-[#D4AF37]" />
                  <p className="text-2xl font-bold text-white">{referralStats.totalReferrals}</p>
                  <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Total Referrals</p>
                </div>
                <div className="text-center p-3 bg-[rgba(30,30,35,0.8)] rounded-lg border border-[rgba(212,175,55,0.2)]">
                  <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-2xl font-bold text-white">{referralStats.successfulReferrals}</p>
                  <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Successful</p>
                </div>
                <div className="text-center p-3 bg-[rgba(30,30,35,0.8)] rounded-lg border border-[rgba(212,175,55,0.2)]">
                  <Users className="w-8 h-8 mx-auto mb-2 text-orange-400" />
                  <p className="text-2xl font-bold text-white">{referralStats.pendingReferrals}</p>
                  <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Pending</p>
                </div>
                <div className="text-center p-3 bg-[rgba(30,30,35,0.8)] rounded-lg border border-[rgba(212,175,55,0.2)]">
                  <Award className="w-8 h-8 mx-auto mb-2 text-[#D4AF37]" />
                  <p className="text-2xl font-bold text-[#D4AF37]">{referralStats.creditsEarned}</p>
                  <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Credits Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How it Works */}
          <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
            <CardHeader>
              <CardTitle style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37] text-black flex items-center justify-center text-sm font-bold">1</span>
                  <span>Share your unique referral link with colleagues and friends</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37] text-black flex items-center justify-center text-sm font-bold">2</span>
                  <span>When they sign up using your link, they get 20 trial credits</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37] text-black flex items-center justify-center text-sm font-bold">3</span>
                  <span>Once they subscribe to a paid plan, you receive 50+ bonus credits</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37] text-black flex items-center justify-center text-sm font-bold">4</span>
                  <span>Your bonus credits roll over every month—they never expire!</span>
                </li>
              </ol>
            </CardContent>
          </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferralProgram;
